import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';

import { canonicalEmail, checkEmail } from '@psy/validations';

import { useLanguage } from '../../contexts/language';
import { useField, validateAll } from '../../hooks/field';
import { useRequest } from '../../hooks/request';
import { useVisit } from '../../hooks/visit';

import { Button } from '../button';
import { Centered } from '../centered';
import { PageTitle } from '../page-title';
import { TextField } from '../text-field';

import styles from './style.module.css';

// The one door in, for new and returning people alike: an address, then a
// code. The screen never says whether the address already has an account,
// because the service answers the same either way.
//
// The address is checked here before anything is sent, with the rule the
// service also applies. It travels to the code screen in navigation state
// rather than in the URL, so it does not end up in history or a shared link.
export function SignIn() {
  const { text, translate } = useLanguage();
  const request = useRequest();
  const navigate = useNavigate();
  const capture = useVisit();
  const email = useField({ initial: '', check: checkEmail });
  const [ sending, setSending ] = useState(false);

  const onSubmit = useCallback(function(event) {
    event.preventDefault();

    if (validateAll([ email ]) === false) {
      return null;
    }

    setSending(true);

    // A person who went back while the code was on its way stays where they
    // went (#135); the code has been sent all the same.
    const owns = capture();

    return request({ path: '/auth/code', method: 'POST', body: { email: email.value } }).then(function() {
      if (owns() === true) {
        navigate('/signin/code', { state: { email: canonicalEmail(email.value) } });
      }
    }).catch(function(failed) {
      if (owns() === false) {
        return;
      }

      setSending(false);
      email.refuse(failed.message);
    });
  }, [ email, request, navigate, capture ]);

  return (
    <Centered nameOnPhone={ true }>
      <PageTitle title={ text.signInTitle } intro={ text.signInIntro }/>

      <form className={ styles.form } onSubmit={ onSubmit } noValidate>
        <TextField
          ref={ email.ref }
          id='sign-in-email'
          label={ text.email }
          type='email'
          value={ email.value }
          onChange={ email.onChange }
          onBlur={ email.onBlur }
          error={ translate(email.error) }
          autoComplete='email'
          placeholder='name@example.com'
        />

        <Button type='submit' disabled={ sending }>{ text.sendCode }</Button>
      </form>
    </Centered>
  );
}
